import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { readMeetverseQuestionsApiV1QuestionsMeetverseGet } from '@api-client/api-client';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { useEventStreamState } from '@hooks/useEventStreamState';
import { useInfiniteMeetverseQuestions } from '@hooks/useInfiniteMeetverseQuestions';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import { detectorMeetverseKey } from '@lib/cache/feed';
import type { PaginatedResponse, QuestionRead, RankedListMeta } from '@models/api';
import { useAuth } from '@providers';
import { logger } from '@utils/logger';
import { sanitizeError } from '@utils/sanitizeError';

/**
 * Detector cache key, from the `@lib/cache/feed` factory (shared with the
 * `feed.new_top_post` SSE invalidator in useRealtimeInvalidation). See the
 * factory for why it lives outside the `['questions', ...]` tree.
 */
const DETECTOR_QUERY_KEY = detectorMeetverseKey();

/**
 * Detector page size. Three is the cheapest size that lets us answer the
 * binary question "did multiple distinct authors contribute new posts?" — the
 * only signal needed to choose between singular and plural copy on the pill.
 * Wire cost is trivial (~3 KB per 60s poll).
 */
const DETECTOR_PAGE_SIZE = 3;

interface UseNewMeetversePostsIndicatorOptions {
  enabled?: boolean;
}

type UseNewMeetversePostsIndicatorReturn =
  | { hasNewContent: false; acknowledge: () => void }
  | {
      hasNewContent: true;
      latestPosterFirstName: string | null;
      /**
       * True when the new (non-cached, non-self, non-anonymous) posts come from
       * 2+ distinct authors. Drives the singular vs plural copy variant on the pill.
       */
      multipleAuthors: boolean;
      acknowledge: () => void;
    };

/**
 * Drives the "new posts" pill on the Meetverse home feed.
 *
 * Compares a cheap detector (`?per_page=3`) against the cached infinite feed.
 * The detector refreshes live from the `feed.new_top_post` SSE event (the
 * useRealtimeInvalidation handler invalidates DETECTOR_QUERY_KEY → refetch)
 * when SSE is connected, and falls back to a 60s poll only while disconnected.
 *
 * See docs/sse-realtime/new-posts-pill-sse-migration.md (#694).
 */
export function useNewMeetversePostsIndicator({
  enabled = true,
}: UseNewMeetversePostsIndicatorOptions = {}): UseNewMeetversePostsIndicatorReturn {
  const queryClient = useQueryClient();
  const { user, isLoading: isAuthLoading } = useAuth();
  const { trackEvent } = useAmplitudeTracking();
  // SSE drives the detector live via the `feed.new_top_post` handler
  // (invalidate → refetch) when connected; the 60s poll below is only the
  // disconnected fallback. Mirrors useUnreadCount / useNotificationCount.
  const { isConnected } = useEventStreamState();

  const isQueryEnabled = enabled && !isAuthLoading;
  const currentUserId = user?.id ?? null;

  const {
    data: detectorData,
    isError: isDetectorError,
    error: detectorError,
  } = useQuery({
    queryKey: DETECTOR_QUERY_KEY,
    queryFn: async () => {
      const response = await readMeetverseQuestionsApiV1QuestionsMeetverseGet({
        per_page: DETECTOR_PAGE_SIZE,
      });
      return response.data;
    },
    enabled: isQueryEnabled,
    // Poll only when SSE is down; when connected, the FEED_NEW_TOP_POST handler
    // invalidates this query and refetches it (it stays enabled, so invalidate
    // triggers a fetch — a disabled query would not).
    refetchInterval: isConnected ? false : 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  // Surface detector failures. Without this, a failing 60s poll would silently
  // hide the pill and the team would have zero diagnostic signal that the
  // feature stopped working in production.
  useEffect(() => {
    if (!isDetectorError) return;
    logger.error('[NewPostsPill detector] poll failed', sanitizeError(detectorError));
    trackEvent(ANALYTICS_EVENTS.pill_detector_failed, {
      message: detectorError instanceof Error ? detectorError.message : 'unknown',
    });
  }, [isDetectorError, detectorError, trackEvent]);

  // Subscribe to the infinite feed cache without spawning a duplicate fetch.
  // Same query key as the active hook in useHomeFeed, so the cached `questions`
  // array updates reactively as the user scrolls / pages load.
  const { questions, refresh } = useInfiniteMeetverseQuestions({ enabled: false });

  const cachedIds = useMemo(() => new Set(questions.map(q => q.id)), [questions]);

  // The most recent created_at the user has cached. Used below to filter out
  // detector items that are NOT actually new — just personalization-re-ranked
  // older posts that happen to not be in the user's cache.
  // Scan for the max instead of trusting questions[0]: the backend sorts the feed
  // latest-first, but this hook reads the SPLICED projection (select in
  // useInfiniteMeetverseQuestions), and the just-posted splice deliberately pins
  // the user's own post at index 0 even when newer posts sit below it. Reading
  // questions[0] there would regress the watermark and re-admit exactly the
  // re-ranked-older-post class this guard exists to reject. ISO-8601 strings from
  // one backend compare correctly as strings (the predicate below already does).
  const newestCachedCreatedAt = useMemo(() => {
    let newest: string | null = null;
    for (const question of questions) {
      if (newest === null || question.created_at > newest) {
        newest = question.created_at;
      }
    }
    return newest;
  }, [questions]);

  // Filter the detector batch to "truly new, non-self, non-anonymous" items.
  //  - `is_my_question` is the canonical own-post flag. The currentUserId
  //    comparison is a belt-and-suspenders check: backend has been observed
  //    to return is_my_question: false for the user's own posts on
  //    per_page=N queries (see commit 251293e5). The pill must never light
  //    up for the user's own posts even when the BE flag is unreliable.
  //  - The `created_at > newestCachedCreatedAt` check is the precision guard
  //    against personalization re-ranking. Without it, an older post that
  //    just got promoted into the top 3 by the personalization service would
  //    pass the cachedIds check (it was never in cache because it never
  //    ranked high enough before) and incorrectly count as a "new" post,
  //    inflating the multipleAuthors signal.
  const newItems = useMemo(() => {
    const items = detectorData?.data ?? [];
    return items.filter(item => {
      if (cachedIds.has(item.id)) return false;
      if (item.is_my_question) return false;
      if (item.is_anonymous) return false;
      if (currentUserId !== null && item.user?.id === currentUserId) return false;
      if (newestCachedCreatedAt !== null && item.created_at <= newestCachedCreatedAt) {
        return false;
      }
      return true;
    });
  }, [detectorData, cachedIds, currentUserId, newestCachedCreatedAt]);

  const hasNewContent = cachedIds.size > 0 && newItems.length > 0;

  // Distinct named authors among the new items. Two or more = plural copy.
  const multipleAuthors = useMemo(() => {
    const authorIds = new Set(
      newItems.map(item => item.user?.id ?? null).filter((id): id is string => id !== null),
    );
    return authorIds.size >= 2;
  }, [newItems]);

  // Most recent new poster's first name (data is sorted latest-first by BE).
  // Trim + split on any whitespace run + first non-empty token to handle
  // leading/trailing/multiple spaces; whitespace-only `full_name` falls
  // through to null and the pill falls back to "New posts".
  const latestPosterFirstName =
    newItems[0]?.user?.full_name?.trim().split(/\s+/).find(Boolean) ?? null;

  // Fire pill_displayed once per appear cycle. The ref gates duplicate fires
  // when downstream values (multipleAuthors, firstName) change while the pill
  // remains visible, and resets on each disappearance so the next appearance
  // counts as a new event.
  const newItemCount = newItems.length;
  const wasShownRef = useRef(false);
  useEffect(() => {
    if (hasNewContent && !wasShownRef.current) {
      wasShownRef.current = true;
      trackEvent(ANALYTICS_EVENTS.pill_displayed, {
        multipleAuthors,
        hasFirstName: latestPosterFirstName !== null,
        newItemCount,
      });
    } else if (!hasNewContent) {
      wasShownRef.current = false;
    }
  }, [hasNewContent, multipleAuthors, latestPosterFirstName, newItemCount, trackEvent]);

  const acknowledge = useCallback(() => {
    // Order matters. Three steps, in this exact order:
    //
    // 1. cancelQueries — abort any in-flight detector fetch. Without this,
    //    a fetch that started ~50-200ms before the tap will land AFTER the
    //    setQueryData write below and overwrite our empty page, re-lighting
    //    the pill the user just dismissed.
    //
    // 2. setQueryData — write an empty page so the predicate flips to false
    //    synchronously this render. We deliberately do NOT use removeQueries:
    //    that would tear down the active observer and let TanStack Query
    //    immediately re-fetch the detector before refresh() has updated the
    //    infinite cache, which races and can briefly re-show the pill.
    //    Writing an empty page keeps the observer subscribed; the next
    //    refetchInterval tick (60s later) pulls fresh data.
    //
    // 3. refresh — truncate the infinite cache to page 1 + invalidate, then
    //    scroll to top.
    trackEvent(ANALYTICS_EVENTS.pill_tapped, {
      multipleAuthors,
      hasFirstName: latestPosterFirstName !== null,
      newItemCount,
    });
    void queryClient.cancelQueries({ queryKey: DETECTOR_QUERY_KEY });
    // Type the writeback explicitly so future schema drift in the meetverse
    // endpoint (e.g. adding a new required field to PaginatedResponse or
    // RankedListMeta) fails at compile time here, not silently at runtime.
    const cleared: PaginatedResponse<QuestionRead, RankedListMeta> = {
      data: [],
      pagination: {
        page: 1,
        per_page: DETECTOR_PAGE_SIZE,
        total: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false,
      },
      meta: { next_cursor: null },
    };
    queryClient.setQueryData<PaginatedResponse<QuestionRead, RankedListMeta>>(
      DETECTOR_QUERY_KEY,
      cleared,
    );
    // Refresh the feed, then re-arm the detector immediately to close the
    // would-be 60s blackout window: a brand-new post arriving in the next
    // minute would otherwise be trapped until the next polling tick.
    refresh()
      .then(() => queryClient.invalidateQueries({ queryKey: DETECTOR_QUERY_KEY, exact: true }))
      .catch((err: unknown) => {
        logger.error('[NewPostsPill] refresh failed after acknowledge', sanitizeError(err));
        trackEvent(ANALYTICS_EVENTS.pill_acknowledge_refresh_failed, {
          message: err instanceof Error ? err.message : 'unknown',
        });
      });
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [queryClient, refresh, trackEvent, multipleAuthors, latestPosterFirstName, newItemCount]);

  if (!hasNewContent) {
    return { hasNewContent: false, acknowledge };
  }
  return {
    hasNewContent: true,
    latestPosterFirstName,
    multipleAuthors,
    acknowledge,
  };
}

export { DETECTOR_QUERY_KEY };
